<template>
  <div class="tags-container">
    <tag-item
      v-b-tooltip.hover
      size="medium"
      :title="gradingDetails?.rubric?.name"
      :label="gradingDetails?.rubric?.name || ''"
      @click.prevent="openRubricPreview(gradingDetails?.rubric)"
    >
      <eg-icon icon="star" size="small" />
    </tag-item>
    <tag-item
      v-b-tooltip.hover
      size="medium"
      :title="
        gradingDetails?.sourceMaterial?.title ? $t('essays.promptMaterial') : ''
      "
      :label="
        gradingDetails?.sourceMaterial?.title || $t('essays.noSourceMaterial')
      "
      @click.prevent="
        openSourceMaterialsPreview(gradingDetails?.sourceMaterialId)
      "
    />
    <tag-item
      v-b-tooltip.hover
      size="medium"
      :title="
        gradingDetails?.customInstructions?.length
          ? $t('essays.customInstructionsPreview', {
              text: truncateWords(gradingDetails?.customInstructions),
            })
          : ''
      "
      :label="
        gradingDetails?.customInstructions || $t('essays.noCustomInstructions')
      "
      @click.prevent="
        openCustomInstructionsPreview(gradingDetails?.customInstructions)
      "
    />
    <tag-item
      v-if="!showAllTags"
      size="medium"
      :label="$t('common.more')"
      @click.prevent="showTags"
    >
      <eg-icon icon="plus-add-more-maximize" size="small" />
    </tag-item>
    <template v-else>
      <eg-grading-details-level :grading-details-level="gradingDetails.level" />
      <tag-item
        v-if="inputLanguage"
        v-b-tooltip.hover
        :title="$t('common.language')"
        :label="inputLanguage?.text || ''"
        size="medium"
      >
        <img
          v-if="inputLanguage.icon"
          width="16"
          :src="require(`@/assets/icons/flags/${inputLanguage.icon}.svg`)"
          :alt="inputLanguage.text"
        />
      </tag-item>
      <tag-item
        v-if="outputLanguage"
        v-b-tooltip.hover
        :title="$t('essays.outputLanguage')"
        :label="outputLanguage?.text || ''"
        size="medium"
      >
        <img
          v-if="outputLanguage.icon"
          width="12"
          :src="require(`@/assets/icons/flags/${outputLanguage.icon}.svg`)"
          :alt="outputLanguage.text"
        />
      </tag-item>
      <tag-item
        v-b-tooltip.hover
        :title="$t('rubrics.gradeIntensity')"
        :label="getIntensityDisplayText(gradingDetails.intensity) || ''"
        size="medium"
      />
      <tag-item
        v-b-tooltip.hover
        :title="$t('rubrics.feedbackDetailLevel')"
        :label="getGradingLengthDisplayText(gradingDetails.length) || ''"
        size="medium"
      />
    </template>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import TagItem from "./tag-item.vue";
import { capitalize } from "@/utils/capitalize";
import { openRubricPreview } from "@/components/dashboard/components/open_rubric_preview";
import {
  getIntensityDisplayText,
  getGradingLengthDisplayText,
} from "@/utils/format";
import EgGradingDetailsLevel from "@/components/global/eg/eg-tags/eg_grading_details_level.vue";
import { truncateWords } from "@/components/folders/truncateWords";
import CustomInstructionsPreviewModal from "@/components/rubrics/gradingDetails/custom_instructions_preview_modal.vue";
import CreateEditSourceMaterialModal from "@/components/source-materials/create_edit_source_material_modal.vue";

export default {
  name: "FolderTags",
  components: {
    TagItem,
    EgGradingDetailsLevel,
  },
  inject: ["workspacesProvider"],
  props: {
    gradingDetails: {
      type: Object,
      default: () => {
        return {
          length: "normal",
          intensity: "moderate",
          level: "middleschool",
          customInstructions: null,
          assignmentId: null,
          assignment: null,
          rubricId: null,
          rubric: null,
          inputLanguageId: null,
          inputLanguage: null,
          outputLanguageId: null,
          outputLanguage: null,
        };
      },
    },
  },

  data() {
    return {
      showAllTags: false,
    };
  },

  computed: {
    ...mapGetters("languages", ["getLanguagesListMap"]),
    inputLanguage() {
      if (!this.gradingDetails?.inputLanguageId) {
        return;
      }
      return this.getLanguagesListMap[this.gradingDetails?.inputLanguageId];
    },
    outputLanguage() {
      if (!this.gradingDetails?.outputLanguageId) {
        return;
      }

      return this.getLanguagesListMap[this.gradingDetails?.outputLanguageId];
    },
  },

  watch: {
    gradingDetails() {
      this.showAllTags = false;
    },
  },
  methods: {
    truncateWords,
    capitalize,
    openRubricPreview,
    getIntensityDisplayText,
    getGradingLengthDisplayText,
    openCustomInstructionsPreview(customInstructions) {
      if (!customInstructions) {
        return;
      }
      this.$egModal.show("custom-instructions-preview-modal", {
        component: CustomInstructionsPreviewModal,
        props: {
          customInstructions: customInstructions,
        },
      });
    },
    openSourceMaterialsPreview(sourceMaterialId) {
      if (!sourceMaterialId) {
        return;
      }
      this.$egModal.show("create-edit-source-material-modal", {
        component: CreateEditSourceMaterialModal,
        props: {
          sourceMaterialId: sourceMaterialId,
          readOnly: true,
        },
        on: {
          onSave: () => {
            this.onShouldLoadFolderHandler();
          },
        },
      });
    },
    onShouldLoadFolderHandler() {
      this.workspacesProvider.loadFolderById(
        this.$route.params.workspaceKey,
        +this.$route.params.id
      );
    },
    showTags() {
      this.showAllTags = true;
    },
  },
};
</script>

<style scoped>
.tags-container {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
</style>

<style lang="scss">
.tooltip .tooltip-inner {
  border-radius: 8px;
  background: var(--color-surface-primary-dark);
  color: var(--color-text-on-surface-disabled);
  font-size: var(--typography-font-size-fs-300);
}
</style>
